import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import { useToast } from 'OtherReusableFunctionality/ToastContext';

const AddProductModal = ({ open, onClose, onProductAdded }) => {
  const theme = useTheme();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);

  // Check if user is Company type
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const companyDetails = apiService.getCompanyDetails();
const [units, setUnits] = useState([]);
const [loadingUnits, setLoadingUnits] = useState(false);

  // Company and Category states
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    company: isCompanyUser ? companyId : '',
    category: '',
    brand: '1', // Default brand ID
    vendor: '1', // Default vendor ID
    name: '',
    ingredients: '',
    description: '',
    shelf_life: '',
    sku: '',
    hsn_code: '',
    gst: '',
    unit_of_measure: '',
    purchase_unit_of_measure: '',
    farmer_price: '',
    mrp: ''
  });

  // Available units
  // const units = [
  //   "Units", "Kg", "Grams", "Liters", "ML", "Pieces", "Boxes", "Packets","No's", "Kit"
  // ];

const fetchMeasureUnits = async () => {
  setLoadingUnits(true);
  try {
    const response = await apiService.get(API_ENDPOINTS.GET_measure_units);
    if (response?.success) {
      const fetchedUnits = response.data?.MeasureUnits || [];
      setUnits(fetchedUnits);

      // ✅ Set defaults if not already set
   if (fetchedUnits.length > 0) {
  setFormData(prev => ({
    ...prev,
    unit_of_measure: '',
    purchase_unit_of_measure: ''
  }));
}

    } else {
      setUnits([]);
    }
  } catch (error) {
    console.error('Error fetching measure units:', error);
    setUnits([]);
  } finally {
    setLoadingUnits(false);
  }
};



  // Fetch companies
  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      // If Company user, create a company array with their company details
      if (isCompanyUser && companyDetails) {
        setCompanies([companyDetails]);
      } else {
        const response = await apiService.get(API_ENDPOINTS.GET_company);
        if (response?.success) {
          setCompanies(response.data?.Company || []);
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies');
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Fetch categories based on company
  const fetchCategories = async (companyId) => {
    if (!companyId) {
      setCategories([]);
      return;
    }

    setLoadingCategories(true);
    try {
      const userId = localStorage.getItem('user_id');
      const response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${companyId}&user_id=${userId}`);
      
      if (response?.success) {
        setCategories(response.data?.ProductCategory || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Reset form when modal opens/closes
useEffect(() => {
  if (open) {
    // Reset form, images, etc.
setFormData({
  company: isCompanyUser ? companyId : '',
  category: '',
  brand: '1',
  vendor: '1',
  name: '',
  ingredients: '',
  description: '',
  shelf_life: '',
  sku: '',
  hsn_code: '',
  gst: '',
  unit_of_measure: 'Units', // ✅ default
  purchase_unit_of_measure: 'Units', // ✅ default
  farmer_price: '',
  mrp: ''
});


    setUploadedImages([]);
    setCategories([]);
    setError('');
    setSuccess('');

    fetchCompanies();
    fetchMeasureUnits(); // 👈 add this line

    if (isCompanyUser && companyId) {
      fetchCategories(companyId);
    }
  }
}, [open, isCompanyUser, companyId]);


  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If company changes, reset category and fetch new categories
    if (field === 'company') {
      setFormData(prev => ({
        ...prev,
        category: '' // Reset category when company changes
      }));
      fetchCategories(value);
    }
    
    setError(''); // Clear error when user types
  };

  // Image upload with react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 5,
    onDrop: (acceptedFiles) => {
      const newImages = acceptedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        id: Math.random().toString(36).substr(2, 9)
      }));
      setUploadedImages(prev => [...prev, ...newImages].slice(0, 5)); // Max 5 images
    }
  });

  // Remove image
  const removeImage = (imageId) => {
    setUploadedImages(prev => {
      const updated = prev.filter(img => img.id !== imageId);
      // Revoke URL to prevent memory leaks
      const removedImage = prev.find(img => img.id === imageId);
      if (removedImage) {
        URL.revokeObjectURL(removedImage.preview);
      }
      return updated;
    });
  };

  // Validate form
  const validateForm = () => {
    const required = ['company', 'category', 'name', 'sku', 'hsn_code', 'gst', 'farmer_price', 'mrp', 'unit_of_measure','purchase_unit_of_measure'];
    const missing = required.filter(field => !formData[field]?.toString().trim());
    
    if (missing.length > 0) {
      setError(`Please fill in all required fields: ${missing.join(', ')}`);
      return false;
    }

    if (uploadedImages.length === 0) {
      setError('Please upload at least one product image');
      return false;
    }

    if (parseFloat(formData.farmer_price) <= 0 || parseFloat(formData.mrp) <= 0) {
      setError('Prices must be greater than 0');
      return false;
    }

    if (parseFloat(formData.gst) < 0 || parseFloat(formData.gst) > 100) {
      setError('GST rate must be between 0 and 100');
      return false;
    }

    return true;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      // Create FormData
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });

      // Add images
      uploadedImages.forEach(image => {
        submitData.append('images', image.file);
      });

      // Submit to API
      const response = await apiService.post(
        API_ENDPOINTS.POST_add_product,
        submitData,
        "multipart/form-data"
      );

      if (response?.success) {
        setSuccess('Product added successfully!');
        toast.showSuccess('Product added successfully!');
        setTimeout(() => {
          onProductAdded();
        }, 1500);
      } else {
        setError(response?.message || 'Failed to add product');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      setError(error.message || 'An error occurred while adding the product');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
<Dialog
  open={open}
  onClose={onClose}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 2,
      overflow: 'hidden' // prevents outer scroll
    }
  }}
>

      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Add New Product
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

     <DialogContent
  dividers
  sx={{
    p: 3,
    maxHeight: '80vh', // or '70vh' depending on how tall your title/actions are
    overflowY: 'auto'   // only inner content scrolls
  }}
>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          {/* Company Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Company</InputLabel>
              <Select
                value={formData.company}
                label="Company"
                onChange={(e) => handleInputChange('company', e.target.value)}
                disabled={isSubmitting || loadingCompanies || isCompanyUser}
              >
                {loadingCompanies ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading companies...
                  </MenuItem>
                ) : (
                  companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Category Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => handleInputChange('category', e.target.value)}
                disabled={isSubmitting || loadingCategories || !formData.company}
              >
                {!formData.company ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      Select a company first
                    </Typography>
                  </MenuItem>
                ) : loadingCategories ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading categories...
                  </MenuItem>
                ) : categories.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No categories available for this company
                    </Typography>
                  </MenuItem>
                ) : (
                  categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Product Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isSubmitting}
              placeholder="Enter product name"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="SKU"
              value={formData.sku}
              onChange={(e) => handleInputChange('sku', e.target.value.toUpperCase())}
              disabled={isSubmitting}
              placeholder="e.g., PROD-001"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="HSN Code"
              value={formData.hsn_code}
              onChange={(e) => handleInputChange('hsn_code', e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g., 38083040"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={isSubmitting}
              placeholder="Describe the product features and benefits"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Ingredients"
              value={formData.ingredients}
              onChange={(e) => handleInputChange('ingredients', e.target.value)}
              disabled={isSubmitting}
              placeholder="List main ingredients (optional)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Shelf Life"
              value={formData.shelf_life}
              onChange={(e) => handleInputChange('shelf_life', e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g., 2 years from manufacturing date"
            />
          </Grid>

          {/* Pricing & Units */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
              Pricing & Units
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="Farmer Price (₹)"
              value={formData.farmer_price}
              onChange={(e) => handleInputChange('farmer_price', e.target.value)}
              disabled={isSubmitting}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="MRP (₹)"
              value={formData.mrp}
              onChange={(e) => handleInputChange('mrp', e.target.value)}
              disabled={isSubmitting}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="GST Rate (%)"
              value={formData.gst}
              onChange={(e) => handleInputChange('gst', e.target.value)}
              disabled={isSubmitting}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
<FormControl fullWidth>
  <InputLabel>Unit of Measure *</InputLabel>
  <Select
    value={formData.unit_of_measure}
    label="Unit of Measure"
    onChange={(e) => handleInputChange('unit_of_measure', e.target.value)}
    disabled={isSubmitting || loadingUnits}
  >
    {loadingUnits ? (
      <MenuItem disabled>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        Loading units...
      </MenuItem>
    ) : units.length === 0 ? (
      <MenuItem disabled>No units available</MenuItem>
    ) : (
      units.map((unit) => (
        <MenuItem key={unit.id || unit.name} value={unit.name}>
          {unit.name}
        </MenuItem>
      ))
    )}
  </Select>
</FormControl>

          </Grid>

          <Grid item xs={12} md={6}>
<FormControl fullWidth>
  <InputLabel>Purchase Unit *</InputLabel>
  <Select
    value={formData.purchase_unit_of_measure}
    label="Purchase Unit"
    onChange={(e) => handleInputChange('purchase_unit_of_measure', e.target.value)}
    disabled={isSubmitting || loadingUnits}
  >
    {loadingUnits ? (
      <MenuItem disabled>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        Loading units...
      </MenuItem>
    ) : units.length === 0 ? (
      <MenuItem disabled>No units available</MenuItem>
    ) : (
      units.map((unit) => (
        <MenuItem key={unit.id || unit.name} value={unit.name}>
          {unit.name}
        </MenuItem>
      ))
    )}
  </Select>
</FormControl>

          </Grid>

          {/* Image Upload */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
              Product Images
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Box
              {...getRootProps()}
              sx={{
                border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.grey[100], 0.3),
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              <input {...getInputProps()} disabled={isSubmitting} />
              <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop images here' : 'Upload Product Images'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag & drop images here, or click to select files
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Maximum 5 images • JPG, PNG, GIF, WebP
              </Typography>
            </Box>
          </Grid>

          {/* Image Preview */}
          {uploadedImages.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Uploaded Images ({uploadedImages.length}/5)
              </Typography>
              <Grid container spacing={2}>
                {uploadedImages.map((image) => (
                  <Grid item xs={6} sm={4} md={3} key={image.id}>
                    <Card sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="120"
                        image={image.preview}
                        alt="Product preview"
                        sx={{ objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeImage(image.id)}
                        disabled={isSubmitting}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: alpha(theme.palette.error.main, 0.8),
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.error.main
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          {isSubmitting ? 'Adding Product...' : 'Add Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProductModal; 